import {
  CommonModule,
  IMAGE_LOADER,
  isPlatformBrowser,
  NgOptimizedImage,
  registerLocaleData,
} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import { APP_ID, APP_INITIALIZER, LOCALE_ID, NgModule, PLATFORM_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { CloudinaryModule } from '@cloudinary/ng';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  BaseSiteToBrandService,
  ComponentsModule as HabaBaseComponentsModule,
  CookieModule,
  HabaAuthStatePersistenceService,
  HabaBasePageMetaResolver,
  SVG_PATH_GETTER,
  SvgGetter,
} from 'haba-base-components';
import {
  APP_CLIENT,
  AuthStatePersistenceService,
  BASE_SITE_CONTEXT_ID,
  BasePageMetaResolver,
  BaseSiteService,
  ContextServiceMap,
  CURRENCY_CONTEXT_ID,
  CurrencyService,
  EMPORIX_PRODUCT_NORMALIZER,
  LANGUAGE_CONTEXT_ID,
  LanguageService,
  OAuthLibWrapperService,
  PageMetaResolver,
  SiteContextConfigInitializer,
} from 'haba-core';
import {
  API_URL,
  EmporixSdkService,
  PREFIXBOX_ENVIRONMENT_TRACKERS,
  PREFIXBOX_NEW_ARRIVALS_CATEGORY_ID,
  PrefixboxFilterCustomization,
  PrefixboxModule,
} from 'haba-emporix';
import { PersonalizationFilterCustomization } from './features/search/filter/personalization-filter';
import { ToastrModule } from 'ngx-toastr';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AddToCartPopupComponent } from './features/cart/add-to-cart-popup/add-to-cart-popup.component';
import { AppService } from './app.service';

import { MainPipeModule } from '@app/helpers';
import {
  CloudinaryService,
  CustomSiteContext,
  HabaBaseSiteService,
  HabaSiteContextConfigInitializer,
  HeaderAndFooterService,
  httpInterceptorsProvider,
  LoaderService,
  SeoService,
  StatusCodeResponseService,
} from '@app/services';
import {
  APP_CONTENTFUL_ENVIRONMENT,
  CLOUDINARY_SERVICE,
  cloudinaryLoaderFactory,
  ComponentsModule as HabaContentfulComponentsModule,
} from 'haba-contentful-components';
import { environment } from '../environments/environment';
import { CheckoutHeaderModule } from './contentful/checkout-header/checkout-header.module';
import { ContentfulModule } from './contentful/contentful.module';
import { FeaturesModule } from './features/features.module';
import { InvoiceDownloadModule } from './features/invoice-download/invoice-download.module';
import { NavigationModule } from './features/navigation/navigation.module';
import { CustomProductNormalizer } from './features/product/custom-url/custom-product.normalizer';
import { SharedModule } from './features/shared/shared.module';
import { HabaOAuthLibWrapperService } from './features/user/services/oauth-lib-wrapper.service';
import { SpartacusModule } from './spartacus/spartacus.module';
import { SvgComponent, commonIcons } from '@app/base-components';
import { CheckoutFooterComponent } from './features/checkout/checkout-footer/checkout-footer.component';
import {
  RecaptchaV3Module,
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaFormsModule,
  RecaptchaModule,
  RECAPTCHA_SETTINGS,
  RecaptchaSettings,
} from 'ng-recaptcha';
import { Logger } from '@opentelemetry/api-logs';
import { createOtelLogger, OTEL_LOGGER } from './utils/logging/logging';
import { patchConsoleWithOtel } from './utils/logging/console-bridge';

registerLocaleData(localeDe);
registerLocaleData(localeFr);

export function serviceMapFactory() {
  return {
    [LANGUAGE_CONTEXT_ID]: LanguageService,
    [CURRENCY_CONTEXT_ID]: CurrencyService,
    [BASE_SITE_CONTEXT_ID]: BaseSiteService,
    custom: CustomSiteContext,
  };
}

export function initServices(appService: AppService, logger: Logger) {
  return () => {
    patchConsoleWithOtel(logger);
    appService.initAppServices();
    appService.initThirdPartyServices();
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),
    SpartacusModule,
    FeaturesModule,
    BrowserModule,
    CloudinaryModule,
    FormsModule,
    ReactiveFormsModule,
    MainPipeModule,
    CommonModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    HabaContentfulComponentsModule,
    HabaBaseComponentsModule,
    CookieModule.forRoot(),
    NavigationModule,
    ContentfulModule,
    SharedModule,
    ServiceWorkerModule.register('haba-service-worker.js', { enabled: environment.production }),
    RecaptchaModule,
    RecaptchaFormsModule,
    RecaptchaV3Module,
    InvoiceDownloadModule,
    CheckoutHeaderModule,
    CheckoutFooterComponent,
    // Restrict extension to log-only mode
    StoreDevtoolsModule.instrument({ maxAge: 25, connectInZone: true }),
    PrefixboxModule,
    SvgComponent,
    NgOptimizedImage,
    AddToCartPopupComponent,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (emporixSdkService: EmporixSdkService, platformId: object) => () => {
        // Dynamic import: @opentelemetry/context-zone side-loads a browser zone.js,
        // which must never be evaluated on the server (it breaks the SSR process).
        if (isPlatformBrowser(platformId)) {
          return import('./utils/tracing/tracing').then(({ setupTracing }) =>
            setupTracing(emporixSdkService)
          );
        }
        return undefined;
      },
      deps: [EmporixSdkService, PLATFORM_ID],
      multi: true,
    },
    {
      provide: OTEL_LOGGER,
      useFactory: () =>
        createOtelLogger({
          serviceName: environment.opentelemetry.serviceName,
          environment: environment.opentelemetry.environment,
        }),
    },
    { provide: APP_ID, useValue: 'spartacus-app' },
    { provide: API_URL, useValue: environment.emporixsdk.apiUrl },
    { provide: APP_CLIENT, useValue: environment.client },
    { provide: PREFIXBOX_ENVIRONMENT_TRACKERS, useValue: environment.prefixbox },
    { provide: PrefixboxFilterCustomization, useClass: PersonalizationFilterCustomization },
    { provide: PREFIXBOX_NEW_ARRIVALS_CATEGORY_ID, useValue: '2ab3o1' },
    { provide: APP_CONTENTFUL_ENVIRONMENT, useValue: environment.contentful },
    StatusCodeResponseService,
    BaseSiteToBrandService,
    HeaderAndFooterService,
    {
      provide: ContextServiceMap,
      useFactory: serviceMapFactory,
    },
    {
      provide: SiteContextConfigInitializer,
      useClass: HabaSiteContextConfigInitializer,
    },
    {
      provide: AuthStatePersistenceService,
      useClass: HabaAuthStatePersistenceService,
    },
    httpInterceptorsProvider,
    { provide: CLOUDINARY_SERVICE, useExisting: CloudinaryService },
    {
      provide: APP_INITIALIZER,
      useFactory: initServices,
      deps: [AppService, OTEL_LOGGER],
      multi: true,
    },
    CustomProductNormalizer,
    LoaderService,
    // {
    //   provide: CHECKOUT_DATA_SERVICE,
    //   useClass: CheckoutDataService,
    // },
    // {
    //   provide: LOADER_TOKEN,
    //   useExisting: LoaderService,
    // },
    // {
    //   provide: CustomProductNormalizerToken,
    //   useExisting: CustomProductNormalizer,
    // },
    {
      provide: EMPORIX_PRODUCT_NORMALIZER,
      useExisting: CustomProductNormalizer,
      multi: true,
    },
    {
      provide: PageMetaResolver,
      useExisting: SeoService,
      multi: true,
    },
    {
      provide: BasePageMetaResolver,
      useExisting: HabaBasePageMetaResolver,
    },
    {
      provide: OAuthLibWrapperService,
      useClass: HabaOAuthLibWrapperService,
    },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: environment.recaptcha.v2,
        language: 'de',
      } as RecaptchaSettings,
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.v3,
    },
    {
      provide: BaseSiteService,
      useExisting: HabaBaseSiteService,
    },
    provideHttpClient(withFetch()),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: SVG_PATH_GETTER,
      useFactory: () =>
        <SvgGetter>{
          get: (svgName: string, assetsPath: string, excludeCommonIcon?: boolean) => {
            return svgName.length
              ? commonIcons.includes(svgName) && !excludeCommonIcon
                ? `common${assetsPath || '/assets/icons'}/${svgName}.svg#svg`
                : `${environment.client}${assetsPath || '/assets/icons'}/${svgName}.svg#svg`
              : '';
          },
        },
    },
    {
      provide: IMAGE_LOADER,
      useFactory: () => cloudinaryLoaderFactory(),
      deps: [],
    },
    // this is only used on the currencyPipe, the translations go outside of it.
    { provide: LOCALE_ID, useValue: 'de-DE' },
  ],
})
export class AppModule {}
